import React, { useCallback, useEffect, useState } from "react";
import axios from "../../../api";
import {
  Button,
  TextField,
  Grid,
  Box,
  Modal,
  Autocomplete,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { AxiosError } from "axios";
const customScrollbarStyle = {
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461", // Lighter black color
    borderRadius: "8px", // Curved border
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#666", // Lighter black color on hover
  },
};

const validationSchema = Yup.object({
  spare_part_name: Yup.string().required("Spare Part name is required"),
  asset_type_id: Yup.string().required("Asset Type is required"),
  sku: Yup.string().required("SKU is required"),
  price: Yup.number().required("Price is required"),
  quantity: Yup.number().required("Stock is required"),
  availability_status: Yup.string().required("Availability is required"),
});
interface Spareparts {
  spare_part_id: number;
  spare_part_name: string;
  asset_type_id: number;
  quantity: number;
  price: number;
  availability_status: string;
  sku: string;
}
interface EditSparePartsProps {
  isOpen: boolean;
  assetData: Spareparts | null;
  onClose: () => void;
  fetchData: () => Promise<void>;
}
interface Assets {
  asset_type_id: number;
  asset_type_name: string;
}
const EditSparePart = ({
  isOpen,
  onClose,
  assetData,
  fetchData,
}: EditSparePartsProps) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [assets, setAssets] = useState<Assets[]>([]);

  const fetchAssets = useCallback(async () => {
    try {
      const response = await axios.get("/asset-types", {
        params: { is_active: 1 },
      });
      setAssets(response.data.list ?? []);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="otp-modal"
      aria-describedby="otp-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          minWidth: 500,
          minHeight: 220,
          maxHeight: "calc(100vh - 100px)",
          overflow: "auto",
          ...customScrollbarStyle,
        }}
      >
        <Typography variant="h5" style={{ marginBottom: "18px" }}>
          Edit Spare Part
        </Typography>
        <Formik
          initialValues={{
            spare_part_id: assetData?.spare_part_id ?? "",
            spare_part_name: assetData?.spare_part_name ?? "",
            asset_type_id: assetData?.asset_type_id ?? "",
            quantity: assetData?.quantity ?? "",
            price: assetData?.price ?? "",
            availability_status: assetData?.availability_status ?? "",
            sku: assetData?.sku ?? "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            setSubmitting(true);

            try {
              try {
                const response = await axios.put(
                  `/spare-parts/${assetData?.spare_part_id}`,
                  values
                );
                fetchSuccess(response.data.message);
                fetchData();
                onClose();
                setSubmitting(false);
              } catch (error) {
                if (error instanceof AxiosError && error.response) {
                  fetchError(error.response.data.error);
                }
              } finally {
                setSubmitting(false);
              }
            } catch (error) {
              console.log("error", error);
              if (error instanceof AxiosError && error.response) {
                fetchError(error.response.data.error);
              }
              setSubmitting(false);
            }
          }}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Spare Part
                    </SoftTypography>
                    <Field
                      name="spare_part_name"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={
                        !!errors.spare_part_name && touched.spare_part_name
                      }
                      helperText={
                        touched.spare_part_name && errors.spare_part_name
                      }
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    Asset Type
                  </SoftTypography>
                  <Autocomplete
                    options={assets ?? []}
                    getOptionLabel={(option) => option.asset_type_name ?? ""}
                    value={
                      assets.find(
                        (asset) => asset.asset_type_id === values.asset_type_id
                      ) ?? null
                    }
                    onChange={(event, value) => {
                      setFieldValue(
                        "asset_type_id",
                        value ? value.asset_type_id : ""
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        error={!!errors.asset_type_id && touched.asset_type_id}
                        helperText={
                          touched.asset_type_id && errors.asset_type_id
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      SKU
                    </SoftTypography>
                    <Field
                      name="sku"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={!!errors.sku && touched.sku}
                      helperText={touched.sku && errors.sku}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Price
                    </SoftTypography>
                    <Field
                      name="price"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={!!errors.price && touched.price}
                      helperText={touched.price && errors.price}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Quantity
                    </SoftTypography>
                    <Field
                      name="quantity"
                      as={TextField}
                      fullWidth
                      variant="outlined"
                      error={!!errors.quantity && touched.quantity}
                      helperText={touched.quantity && errors.quantity}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SoftBox>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Availability
                    </SoftTypography>
                    <Field
                      name="availability_status"
                      as={TextField}
                      select
                      fullWidth
                      variant="outlined"
                      error={
                        !!errors.availability_status &&
                        touched.availability_status
                      }
                      helperText={
                        touched.availability_status &&
                        errors.availability_status
                      }
                    >
                      <MenuItem value="In Stock">In Stock</MenuItem>
                      <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                    </Field>
                  </SoftBox>
                </Grid>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="success-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                    }}
                    variant="contained"
                    disabled={submitting}
                    type="submit"
                    color="success"
                  >
                    Update
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                      ml: 2.5,
                    }}
                    variant="contained"
                    color="warning"
                    onClick={onClose}
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default EditSparePart;
